<template>
    <form @submit.prevent="showApprovedModal(true)">
    <div class="xl">
        <div class="pt-2 pb-1 text-2xl font-bold">Products</div>
        
        <div v-if="refund.receipt_image" class="pb-1">
            <span class="font-bold">Receipt Image</span>

            <div class="p-2 mt-3 relative" style="width: 20%">
                <a :href="'/storage/' + refund.receipt_image" target="_blank">
                    <img :src="'/storage/' + refund.receipt_image" class="img-fluid" />
                </a>
            </div>
        </div>
    </div>

    <div class="rounded-t">
        <table class="table w-full">
            <thead>
                <tr class="h-10 align-bottom bg-gray-light border">
                    <th class="text-center" style="width: 15%;">Product Image</th>
                    <th class="text-left" style="width: 10%;">Product</th>
                    <th class="text-left">Defect</th>
                    <th class="text-left">Notes</th>
                    <th class="text-center" style="width: 15%;">Refund Image</th>
                    <th class="text-right whitespace-nowrap">Request Qty</th>
                    <th class="text-right whitespace-nowrap">Refund Qty</th>
                    <th class="text-right">Price</th>
                    <th v-if="!refund.order?.customer?.tax_exempt" class="text-center">
                        Tax
                    </th>
                    <th class="text-right">Total</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item) in refund_items" :key="item.id">
                    <td class="text-left">
                        <img v-if="item.product.image != null" :src="'/storage/' + item.product.image" class="mx-auto max-h-20 border border-transparent rounded-md" alt="Product Image" loading="lazy" />
                        <img v-show="item.product.image == null" src="/images/product-placeholder.png" class="mx-auto max-h-20 rounded-md" alt="No Image" loading="lazy" />
                    </td>
                    <td class="text-left">
                        {{ item.product.title }}
                        <span v-if="item.product.description"> - {{ item.product.description }}</span>
                        <span v-if="item.product.size"> - {{ item.product.size }}</span>
                    </td>
                    <td class="text-left">
                        {{ item.formatted_return_reason }}
                    </td>
                    <td class="text-justify">
                        <div v-if="item.order_item?.parent_id">
                            Part of {{ item.order_item?.parent_item?.title }}
                        </div>
                        {{ item.return_note }}
                    </td>
                    <td class="text-center">
                        <a v-if="item.item_image != null && item.item_image != ''" :href="'/storage/' + item.item_image" target="_blank">
                            <img :src="'/storage/' + item.item_image" class="mx-auto max-h-20 border border-transparent rounded-md" alt="Product Image" loading="lazy" />
                        </a>
                    </td>
                    <td class="text-right">
                        {{ item.quantity }}
                    </td>
                    <td class="text-right">
                        <jet-input-quantity
                            v-if="refund.status === 'new'"
                            type="number"
                            v-model.number="item.refunded_quantity"
                            class="w-full quantity"
                            :step="0.25"
                            :max="item.quantity"
                            :min="0"
                            required
                        />
                        <div v-else>{{ item.refunded_quantity }}</div>
                    </td>
                    <td class="text-right">
                        <div
                            :style="{
                                'padding-top': (item.price != item.order_item?.price) ? '1.25rem' : null,
                                'padding-bottom': (item.price != item.order_item?.price) ? '1.25rem' : null
                            }"
                        >
                            <jet-input
                                v-if="refund.status === 'new'"
                                type="number"
                                class="w-full price text-right"
                                v-model.number="item.price"
                                :step="0.01"
                                required
                            />
                            <div v-else>{{ toCurrency(item.price) }}</div>

                            <div
                                v-if="item.price != item.order_item?.price"
                                class="text-sm text-gray-500"
                                style="margin-bottom: -1.25rem;"
                            >
                                {{ toCurrency(item.order_item?.price) }}
                            </div>
                        </div>
                    </td>
                    <td 
                        class="text-center"
                        v-if="!refund.order?.customer?.tax_exempt"    
                    >
                        <img v-if="item.order_item?.tax" src="/images/icons/check.svg" width="24" height="24" class="mx-auto"/>
                        <img v-else src="/images/icons/x.svg" width="24" height="24" class="mx-auto" />
                    </td>
                    <td class="text-right">
                        {{ toCurrency(parseFloat(item.price * item.refunded_quantity)) }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <div v-if="refund.status !== 'rejected'" class="flex flex-col md:flex-row justify-between">
        <div class="flex"></div>

        <div class="mt-1 self-end">
            <table class="totals">
                <tr>
                    <td class="text-right font-bold">Subtotal:</td>
                    <td class="text-right whitespace-nowrap">
                        <span>{{ toCurrency(subtotal) }}</span>
                    </td>
                </tr>
                <tr v-if="!refund.order?.customer?.tax_exempt">
                    <td class="text-right font-bold">Tax:</td>
                    <td class="text-right whitespace-nowrap">
                        <span>{{ toCurrency(tax) }}</span>
                    </td>
                </tr>
                <tr v-if="refund.order.promo_code_percent && promoCodeAdjustment > 0">
                    <td class="text-right">
                        <div class="font-bold">Promo Code Adjustment:</div>
                        <div class="font-grey-500">
                            {{ refund.order?.applied_promo_code?.code }}
                            <span v-if="refund.order?.promo_code_percent">({{ refund.order.promo_code_percent }}%)</span>
                        </div>
                    </td>
                    <td class="text-right whitespace-nowrap">
                        <span> 
                            -{{ toCurrency(promoCodeAdjustment) }}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td class="text-right font-bold">Refund Total:</td>
                    <td class="text-right whitespace-nowrap">
                        <span v-if="refund.status === 'new'">{{ toCurrency(refundTotal) }}</span>
                        <span v-else>{{ toCurrency(refund.amount) }} </span>
                    </td>
                </tr>
                <tr v-if="refund.status === 'new' && !isCustomer">
                    <th class="px-2 py-1 text-right text-red-600">
                        <jet-button>
                            Refund
                        </jet-button>
                    </th>
                    <td class="px-2 py-1 text-right text-red-600">
                        <jet-danger-button type="button" @click="showRejectedModal(true)">
                            Reject
                        </jet-danger-button>
                    </td>
                </tr>
            </table>
        </div>
    </div>

    <jet-confirmation-modal :show="showingApprovedModal" @close="resetApprovedModal(); showApprovedModal(false)">
        <template #title>
            Approve Refund
        </template>

        <template #content>
            Are you sure you would like to approve this refund?

            <div v-if="requiresRefundMethod">
                <jet-label value="Refund Method" class="mb-0" />
                <multiselect
                    v-model="refundMethod"
                    :options="refundMethodOptions"
                />
                <jet-input-error v-if="refundMethodError" :message="refundMethodError" class="mt-1" />
            </div>

        </template>

        <template #footer>
            <jet-button @click="approveRefundOrder">
                Yes
            </jet-button>

            <jet-tertiary-button class="ml-2" @click="resetApprovedModal(); showApprovedModal(false)">
                No
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>

    <jet-confirmation-modal :show="showingRejectedModal" @close="showRejectedModal(false)">
        <template #title>
            Reject Refund
        </template>

        <template #content>
            Are you sure you would like to reject this refund?
            <textarea 
                v-model="form.reviewed_note"
                class="w-full resize-none mt-2" 
                rows="4" 
                placeholder="Rejection Notes..."
                ref="reviewed_note">
            </textarea>
        </template>

        <template #footer>
            <jet-button @click="rejectRefundOrder">
                Yes
            </jet-button>

            <jet-tertiary-button class="ml-2" @click="showRejectedModal(false)">
                No
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>
    </form>
</template>

<script>
import { usePage, useForm } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputQuantity from '@/Jetstream/InputQuantity';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

export default {
    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return { isAdmin, isCustomer }
    },

    components: {
        JetInput,
        JetInputQuantity,
        JetInputError,
        JetButton,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetLabel,
        JetTertiaryButton,
        Multiselect
    },

    props: [
        'form',
        'refund_items',
        'refund'
    ],

    data() {
        return {
            showingRejectedModal: false,
            showingApprovedModal: false,
            maxRefundAmount: this.form.amount,
            refundMethod: null,
            refundMethodError: null,
            refundMethodOptions: [
                {
                    label: 'Credit Card',
                    value: 'credit_card'
                },
                {
                    label: 'Promo Code',
                    value: 'promo_code'
                }
            ],

            selectedRefundOrderItemId: null,
        }
    },

    computed: {
        requiresRefundMethod () {
            return this.refund.order?.is_wholesale == 0;
        },

        tax () {
            if (this.refund.order?.customer?.tax_exempt) return 0
            let tax = 0
            for (let item of this.refund_items) {
                let itemTax = 0;
                if(item.order_item?.tax){ 
                    itemTax = Number(item.price) * item.refunded_quantity * 0.13;
                }
                tax += itemTax;
            }
            return tax;
        },

        subtotal () {
            let refundAmount = 0
            for (let item of this.refund_items) {
                refundAmount += Number(item.price) * item.refunded_quantity;
            }
            return refundAmount;
        },

        totalBeforeAdjustment () {
             return this.subtotal + this.tax;
        },

        promoCodeAdjustment () {
            let promoCodeMultiplier = 0;

            if (this.refund.order.promo_code_percent){
                promoCodeMultiplier = this.refund.order.promo_code_percent / 100
            }

            return this.totalBeforeAdjustment * promoCodeMultiplier;
        },

        refundTotal(){
            return this.totalBeforeAdjustment - this.promoCodeAdjustment;
        }
    },

    methods: {
        showApprovedModal(value) {
            if(!this.form.processing){
                this.showingApprovedModal = value;
            }
        },

        resetApprovedModal () {
            this.showApprovedModal(false);
            this.refundMethod = null;
            this.refundMethodError = null;
        },

        approveRefundOrder() {
            if (this.requiresRefundMethod && !this.refundMethod) 
            {
                this.refundMethodError = "A refund method is required."
                return false;
            }

            const form = useForm({
                refund_id: this.form.id,
                refunded_amount: this.refundTotal, 
                refund_method: this.refundMethod,
                refund_items: this.refund_items
            })
            this.resetApprovedModal();
            form.post(route('refunds.approve'));
        },

        showRejectedModal(value) {
            this.showingRejectedModal = value;
        },

        rejectRefundOrder() {
            const form = useForm({
                refund_id: this.form.id,
                reviewed_note: this.form.reviewed_note
            })
            this.showRejectedModal(false);
            form.post(route('refunds.reject'));
        },
    }
}
</script>

<style scoped>
.totals th, .totals td {
    padding: 0.25rem 0.5rem ;
}
</style>
